import React, { useCallback, useEffect, useState } from 'react';
import { useLang } from '../i18n/LanguageContext';
import { stayExtensionText } from '../i18n/pages/stayExtension';
import {
  StayExtensionMode,
  StayExtensionOffer,
  StayExtensionSettings as Settings,
  StayExtensionType,
  addStayExtensionOffer,
  deleteStayExtensionOffer,
  getStayExtensionSettings,
  isArrival,
  isLuggage,
  updateStayExtensionSettings,
} from '../api.stayExtension';
import { offerSlotMinutes, minutesOfDayToTime } from '../utils/stayExtensionOfferSlots';
import { apartmentEditNavText } from '../i18n/pages/apartmentEditNav';
import { AdvancedDetails } from './AdvancedDetails';

interface Props {
  apartmentId: number;
  /** Called after a change was accepted by the server, so the page's summary line can refresh. */
  onSaved?: () => void;
}

const MODES: StayExtensionMode[] = ['OFF', 'AUTO', 'REQUEST'];

const INTRO_KEY = 'stay_extension_intro_expanded';

/**
 * Everything this apartment sells alongside the stay itself: the hours either side of it, and
 * storing bags outside them.
 *
 * <p>The two are configured separately rather than under one switch, and that separation is the
 * feature rather than an oversight. Cleaning time is what makes an extra hour hard to give; a hall
 * cupboard costs the host nothing, so a manager who has to refuse every early check-in can still
 * say yes to luggage all season. One shared mode would have silently taken that away.
 *
 * <p>The notice field is the one worth reading twice. Zero is a legitimate setting rather than a
 * way to switch the rule off — the cleaning window, the contention check and the price fence all
 * still apply — so the hint says so rather than leaving a bare number to be guessed at.
 */
const UpsellSettings: React.FC<Props> = ({ apartmentId, onSaved }) => {
  const { lang } = useLang();
  const t = stayExtensionText[lang];
  const advancedTitle = apartmentEditNavText[lang].advanced;

  const [settings, setSettings] = useState<Settings | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Closed until asked for: this already sits behind a collapsed card on the apartment page, so a
  // manager who opened it wants the controls, not a second tour. An explicit "open" is remembered.
  const [introOpen, setIntroOpen] = useState(() => localStorage.getItem(INTRO_KEY) === 'true');

  const load = useCallback(async () => {
    try {
      setSettings(await getStayExtensionSettings(apartmentId));
    } catch {
      setSettings(null);
    }
  }, [apartmentId]);

  useEffect(() => { void load(); }, [load]);

  const patch = async (body: Partial<Settings>) => {
    setSaving(true);
    setError(null);
    try {
      setSettings(await updateStayExtensionSettings(apartmentId, {
        mode: body.mode,
        maxCleaningMinutes: body.maxCleaningMinutes,
        autoApproveNoticeDays: body.autoApproveNoticeDays,
        luggageMode: body.luggageMode,
        // Only sent when the caller actually touched the text: undefined leaves it alone, while ''
        // is a deliberate clear. Coercing null to '' here would wipe it on every unrelated save.
        luggageInstructions: body.luggageInstructions ?? undefined,
        luggageInstructionsEn: body.luggageInstructionsEn ?? undefined,
      }));
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
      onSaved?.();
    } catch (e: any) {
      setError(e?.response?.data?.message || t.actionError);
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return null;

  return (
    <div style={styles.wrap}>
      {/* What this section is, before the controls that do it. Collapsible and remembered,
          matching the guide tab's own intro: useful the first few times, then in the way. */}
      <div style={styles.introCard}>
        <button
          type="button"
          onClick={() => {
            const next = !introOpen;
            setIntroOpen(next);
            localStorage.setItem(INTRO_KEY, String(next));
          }}
          style={styles.introToggle}
        >
          <span style={styles.introToggleLabel}>🕐 {t.introTitle}</span>
          <span style={styles.introChevron}>{introOpen ? '▲' : '▼'}</span>
        </button>

        {introOpen && (
          <div style={{ padding: '0 20px 16px' }}>
            <div style={styles.introDesc}>{t.introDesc}</div>
            <div style={styles.introSteps}>
              {([
                { icon: '🛎️', n: '1', label: t.introStep1Label, desc: t.introStep1Desc, bg: '#ebf8ff', border: '#90cdf4' },
                { icon: '✅', n: '2', label: t.introStep2Label, desc: t.introStep2Desc, bg: '#f0fff4', border: '#9ae6b4' },
                { icon: '💳', n: '3', label: t.introStep3Label, desc: t.introStep3Desc, bg: '#fffaf0', border: '#fbd38d' },
                { icon: '🔑', n: '4', label: t.introStep4Label, desc: t.introStep4Desc, bg: '#faf5ff', border: '#d6bcfa' },
              ]).map(({ icon, n, label, desc, bg, border }) => (
                <div key={n} style={{ ...styles.introStep, background: bg, border: `1px solid ${border}` }}>
                  <div style={styles.introStepHead}>
                    <span style={{ fontSize: '1.3rem' }}>{icon}</span>
                    <span style={styles.introStepLabel}>{n}. {label}</span>
                  </div>
                  <div style={styles.introStepDesc}>{desc}</div>
                </div>
              ))}
            </div>
            <div style={styles.introNote}>💡 {t.introNote}</div>
          </div>
        )}
      </div>

      <h3 className="ae-embedded-title" style={styles.title}>{t.settingsTitle}</h3>
      <p style={styles.intro}>{t.settingsIntro}</p>

      {/* ── Product 1: the hours either side of the stay ───────────────────── */}
      <div style={styles.product}>
        <div style={styles.productHead}>
          <span style={styles.productIcon}>🕐</span>
          <div>
            <div style={styles.productName}>{t.productHours}</div>
            <div style={styles.productDesc}>{t.productHoursDesc}</div>
          </div>
        </div>

        <ModePicker
          value={settings.mode}
          disabled={saving}
          onPick={mode => patch({ mode })}
          hints={[t.modeOffHint, t.modeAutoHint, t.modeRequestHint]}
        />

      {settings.mode !== 'OFF' && (
        <>

          <Stair
            apartmentId={apartmentId}
            type="EARLY_CHECKIN"
            title={t.earlyStairTitle}
            standardTime={settings.standardCheckinTime}
            offers={settings.earlyCheckinOffers}
            onChanged={load}
          />
          <Stair
            apartmentId={apartmentId}
            type="LATE_CHECKOUT"
            title={t.lateStairTitle}
            standardTime={settings.standardCheckoutTime}
            offers={settings.lateCheckoutOffers}
            onChanged={load}
          />
        </>
      )}
      </div>
      <AdvancedDetails title={advancedTitle} count={4}>
        {settings.mode !== 'OFF' && (
          <div style={styles.numbers}>
            <label style={styles.field}>
              <span style={styles.label}>{t.cleaningLabel}</span>
              <span style={styles.inputRow}>
                <input
                  type="number" min={0} step={15} disabled={saving}
                  value={settings.maxCleaningMinutes}
                  onChange={e => setSettings({ ...settings, maxCleaningMinutes: Number(e.target.value) })}
                  onBlur={e => patch({ maxCleaningMinutes: Number(e.target.value) })}
                  style={styles.input}
                />
                <span style={styles.unit}>min</span>
              </span>
              <span style={styles.hint}>{t.cleaningHint}</span>
            </label>

            {settings.mode === 'AUTO' && (
              <label style={styles.field}>
                <span style={styles.label}>{t.noticeLabel}</span>
                <span style={styles.inputRow}>
                  <input
                    type="number" min={0} disabled={saving}
                    value={settings.autoApproveNoticeDays}
                    onChange={e => setSettings({ ...settings, autoApproveNoticeDays: Number(e.target.value) })}
                    onBlur={e => patch({ autoApproveNoticeDays: Number(e.target.value) })}
                    style={styles.input}
                  />
                  <span style={styles.unit}>{t.noticeHint}</span>
                </span>
                <span style={styles.hint}>
                  {settings.autoApproveNoticeDays === 0 ? t.noticeZeroHint : ''}
                </span>
              </label>
            )}
          </div>
        )}

      {/* ── Product 2: somewhere to put the bags ───────────────────────────── */}
      <div style={styles.product}>
        <div style={styles.productHead}>
          <span style={styles.productIcon}>🧳</span>
          <div>
            <div style={styles.productName}>{t.productLuggage}</div>
            <div style={styles.productDesc}>{t.productLuggageDesc}</div>
          </div>
        </div>

        {/* Deliberately never disabled by the hours mode above. The whole point of this product is
            that it is sellable on the days the flat itself is not. */}
        <ModePicker
          value={settings.luggageMode}
          disabled={saving}
          onPick={luggageMode => patch({ luggageMode })}
          hints={[t.luggageOffHint, t.luggageAutoHint, t.luggageRequestHint]}
        />

        {settings.luggageMode !== 'OFF' && (
          <>
            <Stair
              apartmentId={apartmentId}
              type="LUGGAGE_DROPOFF"
              title={t.dropoffStairTitle}
              standardTime={settings.standardCheckinTime}
              offers={settings.luggageDropoffOffers}
              onChanged={load}
            />
            <Stair
              apartmentId={apartmentId}
              type="LUGGAGE_PICKUP"
              title={t.pickupStairTitle}
              standardTime={settings.standardCheckoutTime}
              offers={settings.luggagePickupOffers}
              onChanged={load}
            />

            <LuggageInstructions
              settings={settings}
              saving={saving}
              onChange={next => setSettings({ ...settings, ...next })}
              onSave={patch}
            />
          </>
        )}
      </div>
      </AdvancedDetails>

      {saved && <div style={styles.saved}>{t.saved}</div>}
      {error && <div style={styles.error}>{error}</div>}
    </div>
  );
};

/** The three modes, shared by both products — only the hints differ. */
const ModePicker: React.FC<{
  value: StayExtensionMode;
  disabled: boolean;
  onPick: (mode: StayExtensionMode) => void;
  hints: [string, string, string];
}> = ({ value, disabled, onPick, hints }) => {
  const { lang } = useLang();
  const t = stayExtensionText[lang];
  const names = [t.modeOff, t.modeAuto, t.modeRequest];

  return (
    <>
      <div style={styles.label}>{t.modeLabel}</div>
      <div style={styles.modes}>
        {MODES.map((mode, i) => (
          <button
            key={mode} type="button" disabled={disabled}
            onClick={() => onPick(mode)}
            style={{ ...styles.mode, ...(value === mode ? styles.modeOn : {}) }}
          >
            <span style={styles.modeName}>{names[i]}</span>
            <span style={styles.modeHint}>{hints[i]}</span>
          </button>
        ))}
      </div>
    </>
  );
};

/**
 * Where the bags actually go, in the manager's language and in English.
 *
 * <p>A guest who has paid for storage and is told nothing has bought a receipt, so an empty field
 * while luggage is on is called out rather than left to be discovered by the first guest who buys.
 */
const LuggageInstructions: React.FC<{
  settings: Settings;
  saving: boolean;
  onChange: (next: Partial<Settings>) => void;
  onSave: (body: Partial<Settings>) => void;
}> = ({ settings, saving, onChange, onSave }) => {
  const { lang } = useLang();
  const t = stayExtensionText[lang];
  const missing = !settings.luggageInstructions?.trim();

  return (
    <div style={styles.stair}>
      <div style={styles.stairTitle}>{t.luggageWhereLabel}</div>
      <div style={styles.hint}>{t.luggageWhereHint}</div>

      <textarea
        disabled={saving}
        value={settings.luggageInstructions ?? ''}
        onChange={e => onChange({ luggageInstructions: e.target.value })}
        onBlur={e => onSave({ luggageInstructions: e.target.value })}
        placeholder={t.luggageWherePlaceholder}
        rows={3}
        style={styles.textarea}
      />

      <div style={{ ...styles.label, marginTop: 12 }}>{t.luggageWhereEnLabel}</div>
      <textarea
        disabled={saving}
        value={settings.luggageInstructionsEn ?? ''}
        onChange={e => onChange({ luggageInstructionsEn: e.target.value })}
        onBlur={e => onSave({ luggageInstructionsEn: e.target.value })}
        placeholder={t.luggageWherePlaceholder}
        rows={3}
        style={styles.textarea}
      />

      {missing && <div style={styles.warn}>⚠️ {t.luggageWhereMissing}</div>}
    </div>
  );
};

/** One price stair. Rows are added and removed individually rather than edited as a block. */
const Stair: React.FC<{
  apartmentId: number;
  type: StayExtensionType;
  title: string;
  standardTime: string;
  offers: StayExtensionOffer[];
  onChanged: () => Promise<void>;
}> = ({ apartmentId, type, title, standardTime, offers, onChanged }) => {
  const { lang } = useLang();
  const t = stayExtensionText[lang];

  const [time, setTime] = useState('');
  const [price, setPrice] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stairs read in opposite directions depending on which end they hang off. An arrival-side row
  // at 12:00 means "from 12:00"; a departure-side row at 13:00 means "until 13:00". Labelling both
  // "From" would describe one of them backwards. Asked of the end, not of the constant, so the two
  // luggage types inherit the right direction without another branch.
  const isEarly = isArrival(type);
  const hint = type === 'EARLY_CHECKIN' ? t.stairHintEarly
    : type === 'LATE_CHECKOUT' ? t.stairHintLate
    : type === 'LUGGAGE_DROPOFF' ? t.stairHintDropoff
    : t.stairHintPickup;

  const add = async () => {
    if (!time) return;
    setBusy(true);
    setError(null);
    try {
      await addStayExtensionOffer(apartmentId, { type, time, price: Number(price || 0) });
      setTime('');
      setPrice('');
      await onChanged();
    } catch (e: any) {
      setError(e?.response?.data?.message || t.actionError);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (offerId: number) => {
    setBusy(true);
    try {
      await deleteStayExtensionOffer(apartmentId, offerId);
      await onChanged();
    } catch (e: any) {
      setError(e?.response?.data?.message || t.actionError);
    } finally {
      setBusy(false);
    }
  };

  // The 30-minute grid (spec S2), on the correct side of the standard time — see
  // utils/stayExtensionOfferSlots.ts for why minutes-of-day rather than just the hour.
  const slotMinutes = offerSlotMinutes(standardTime, isEarly, isLuggage(type));

  return (
    <div style={styles.stair}>
      <div style={styles.stairTitle}>{title} <span style={styles.stairStd}>({standardTime})</span></div>
      <div style={styles.hint}>{hint}</div>

      {offers.length === 0 && <div style={styles.noRows}>{t.noRows}</div>}
      {offers.map(offer => (
        <div key={offer.id} style={styles.row}>
          <span style={styles.rowTime}>{offer.time}</span>
          <span style={styles.rowPrice}>{offer.price.toLocaleString()} CZK</span>
          <button type="button" disabled={busy} onClick={() => remove(offer.id)} style={styles.removeBtn}>
            {t.remove}
          </button>
        </div>
      ))}

      <div style={styles.addRow}>
        <select value={time} onChange={e => setTime(e.target.value)} disabled={busy} style={styles.select}>
          <option value="">{isEarly ? t.timeLabelFrom : t.timeLabelTo}</option>
          {slotMinutes.map(minutes => {
            const value = minutesOfDayToTime(minutes);
            return <option key={value} value={value}>{value}</option>;
          })}
        </select>
        <input
          type="number" min={0} step={50} disabled={busy}
          value={price} onChange={e => setPrice(e.target.value)}
          placeholder={t.priceLabel} style={styles.input}
        />
        <button
          type="button" disabled={busy || !time} onClick={add}
          className="app-btn app-btn-blue app-btn-compact"
        >
          {t.addRow}
        </button>
      </div>
      {error && <div style={styles.error}>{error}</div>}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  wrap: { marginTop: 8 },
  introCard: {
    marginBottom: 24, borderRadius: 12, overflow: 'hidden',
    background: 'linear-gradient(135deg, #ebf8ff 0%, #f0fff4 100%)',
    border: '1px solid #bee3f8',
  },
  introToggle: {
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 20px', background: 'none', border: 'none', cursor: 'pointer',
    textAlign: 'left', fontFamily: 'inherit',
  },
  introToggleLabel: { fontWeight: 700, fontSize: '1rem', color: '#1a365d' },
  introChevron: { fontSize: '0.9rem', color: '#4a7cbf', flexShrink: 0, marginLeft: 8 },
  introDesc: { fontSize: '0.88rem', color: '#2d3748', marginBottom: 16, lineHeight: 1.55 },
  introSteps: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 },
  introStep: { borderRadius: 10, padding: '12px 14px' },
  introStepHead: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 },
  introStepLabel: {
    fontWeight: 700, fontSize: '0.82rem', color: '#4a5568',
    textTransform: 'uppercase', letterSpacing: '0.05em',
  },
  introStepDesc: { fontSize: '0.82rem', color: '#4a5568', lineHeight: 1.5 },
  introNote: {
    marginTop: 14, padding: '10px 13px', borderRadius: 8,
    background: 'rgba(255,255,255,.65)', border: '1px solid #bee3f8',
    fontSize: '0.82rem', color: '#2c5282', lineHeight: 1.5,
  },
  title: { fontSize: 16, fontWeight: 700, margin: '0 0 4px' },
  intro: { fontSize: 13, color: '#64748b', margin: '0 0 16px' },
  product: {
    border: '1px solid #e2e8f0', borderRadius: 12, padding: 16,
    marginBottom: 16, background: '#f8fafc',
  },
  productHead: { display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 },
  productIcon: { fontSize: '1.5rem', lineHeight: 1, flexShrink: 0 },
  productName: { fontWeight: 700, fontSize: 15, color: '#1e293b' },
  productDesc: { fontSize: 12.5, color: '#64748b', lineHeight: 1.5, marginTop: 2 },
  textarea: {
    width: '100%', boxSizing: 'border-box', padding: '9px 11px', borderRadius: 8,
    border: '1px solid #e2e8f0', fontSize: 14, fontFamily: 'inherit', resize: 'vertical',
  },
  warn: {
    marginTop: 10, padding: '8px 11px', borderRadius: 8,
    background: '#fffbeb', color: '#92400e', fontSize: 12.5, lineHeight: 1.45,
  },
  label: { display: 'block', fontSize: 12, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: '#94a3b8', marginBottom: 6 },
  modes: { display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', marginBottom: 18 },
  mode: {
    textAlign: 'left', padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
    border: '1px solid #e2e8f0', background: '#fff', fontFamily: 'inherit',
    display: 'flex', flexDirection: 'column', gap: 3,
  },
  modeOn: { borderColor: '#2563eb', borderWidth: 2, background: '#eff6ff' },
  modeName: { fontWeight: 700, fontSize: 14 },
  modeHint: { fontSize: 12, color: '#64748b', lineHeight: 1.4 },
  numbers: { display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', marginBottom: 20 },
  field: { display: 'block' },
  inputRow: { display: 'flex', gap: 8, alignItems: 'center' },
  input: {
    width: 90, padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0',
    fontSize: 14, fontFamily: 'inherit',
  },
  unit: { fontSize: 12, color: '#64748b' },
  hint: { display: 'block', fontSize: 12, color: '#94a3b8', marginTop: 6, lineHeight: 1.45 },
  stair: { border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 12, background: '#fff' },
  stairTitle: { fontWeight: 700, fontSize: 14, marginBottom: 2 },
  stairStd: { fontWeight: 400, color: '#94a3b8' },
  noRows: { fontSize: 13, color: '#94a3b8', padding: '10px 0' },
  row: {
    display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 12, alignItems: 'center',
    padding: '9px 12px', background: '#f8fafc', borderRadius: 8, marginBottom: 6, fontSize: 14,
  },
  rowTime: { fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  rowPrice: { fontVariantNumeric: 'tabular-nums' },
  removeBtn: {
    background: 'transparent', border: 'none', color: '#94a3b8',
    cursor: 'pointer', fontSize: 12, fontFamily: 'inherit',
  },
  addRow: { display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' },
  select: { padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, fontFamily: 'inherit' },
  saved: { fontSize: 13, color: '#16a34a', marginTop: 8 },
  error: { padding: '9px 12px', borderRadius: 8, background: '#fef2f2', color: '#b91c1c', fontSize: 13, marginTop: 8 },
};

export default UpsellSettings;
